export const CALENDAR_LEVELS = ['off', 'light', 'on', 'peak'];
const RANK = { off: 0, light: 1, on: 2, peak: 3 };
export const CALENDAR_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function toMonth(v) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : null;
}

function levelOf(v) {
  return typeof v === 'string' && RANK[v] !== undefined ? v : 'off';
}

function levelsByMonth(months) {
  const out = {};
  if (!Array.isArray(months)) return out;
  for (const row of months) {
    const m = toMonth(row?.m);
    if (m == null) continue;
    const lvl = levelOf(row?.level);
    if (out[m] === undefined || RANK[lvl] > RANK[out[m]]) out[m] = lvl;
  }
  return out;
}

/**
 * Sidecar season_calendar -> render model, or null when nothing usable.
 * Combined strip = max level across origins (never summed).
 */
export function buildCalendarRows(seasonCalendar, { asOfMonth } = {}) {
  if (!seasonCalendar || typeof seasonCalendar !== 'object') return null;
  const rawOrigins = Array.isArray(seasonCalendar.origins) ? seasonCalendar.origins : [];
  const currentMonth = toMonth(asOfMonth) ?? toMonth(seasonCalendar.as_of_month);

  const origins = [];
  for (const o of rawOrigins) {
    const label = typeof o?.origin === 'string' ? o.origin.trim() : '';
    if (!label || !Array.isArray(o.months)) continue;
    const byMonth = levelsByMonth(o.months);
    const cells = CALENDAR_MONTHS.map((m) => ({
      m,
      level: byMonth[m] || 'off',
      isCurrent: m === currentMonth,
    }));
    if (!cells.some((c) => c.level !== 'off')) continue;
    origins.push({ key: label.toLowerCase(), label, cells });
  }
  if (!origins.length) return null;

  const combined = CALENDAR_MONTHS.map((m, i) => {
    let level = 'off';
    for (const o of origins) {
      if (RANK[o.cells[i].level] > RANK[level]) level = o.cells[i].level;
    }
    return { m, level, isCurrent: m === currentMonth };
  });

  return { months: CALENDAR_MONTHS, currentMonth, origins, combined };
}

/** Static Peru/Chile shipping windows (owner: always on, dest-independent). */
export function staticShippingCalendar(asOfMonth) {
  const m = toMonth(asOfMonth) ?? new Date().getUTCMonth() + 1;
  const chileOn = new Set([11, 12, 1, 2, 3, 4]);
  return {
    version: 1,
    as_of_month: m,
    origins: [
      {
        origin: 'Peru',
        months: CALENDAR_MONTHS.map((n) => ({ m: n, level: 'on' })),
      },
      {
        origin: 'Chile',
        months: CALENDAR_MONTHS.map((n) => ({
          m: n,
          level: chileOn.has(n) ? (n === 12 || n === 1 ? 'peak' : 'on') : 'off',
        })),
      },
    ],
  };
}

/** Prefer the pack sidecar (volume-based peak/on/light). Static Peru/Chile only if missing. */
export function calendarRowsForPack(seasonCalendar, opts) {
  const fromPack = buildCalendarRows(seasonCalendar, opts);
  if (fromPack) return fromPack;
  return buildCalendarRows(staticShippingCalendar(opts?.asOfMonth ?? seasonCalendar?.as_of_month), opts);
}

export function monthLabel(m, locale = 'en', style = 'short') {
  const month = toMonth(m);
  if (month == null) return '';
  try {
    return new Intl.DateTimeFormat(locale || 'en', { month: style, timeZone: 'UTC' }).format(
      new Date(Date.UTC(2024, month - 1, 1)),
    );
  } catch {
    return new Intl.DateTimeFormat('en', { month: style, timeZone: 'UTC' }).format(
      new Date(Date.UTC(2024, month - 1, 1)),
    );
  }
}
